import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import GoogleMapReact from "google-map-react";
import { MdMap, MdSatelliteAlt } from "react-icons/md";
import MarkerLabel from "./MarkerLabel";
import Filter from "../Filter/Filter";
import Timeline from "./Timeline";
import MapLoader from "../../../../components/MapLoader/MapLoader";
import {
  MAP_OPTIONS,
  MAP_VIEW_ROADMAP,
  MAP_VIEW_SATELLITE,
  applyMapViewType,
  getInitialMapViewType,
} from "./mapDarkStyles";
import "./Map.css";

// Internal marker component to prevent library $-props from reaching DOM and causing warnings
const Marker = (props) => {
  const { $hover, $getDimensions, $dimensionKey, $geoService, $onMouseAllow, $prerender, children, ...rest } = props; // eslint-disable-line
  return <div {...rest}>{children}</div>;
};

const Map = ({
  user,
  setSelectedEquipment,
  setHistoricEquipment,
  isHistoricMode,
  points,
  medida,
  equipmentsStatus,
  filter,
  cooperative,
  companyNames,
  stateNames,
  typeEquipaments,
  grains,
  activeFilters,
  handleExportAllEquipments,
  currentPeriod,
  handleSearchData,
  highlightedSerials = []
}) => {

  const mapRef = useRef();
  const [center, setCenter] = useState({ lat: -14.2908, lng: -55.9073 });
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [medidaQueue, setMedidaQueue] = useState([]);
  const polylineRef = useRef(null);
  const userInteractedRef = useRef(false);
  const mapLoadedRef = useRef(false); // Referência para controlar se o mapa já foi carregado
  const [mapLoading, setMapLoading] = useState(() => {
    // Verifica se o mapa já foi carregado anteriormente
    const wasLoaded = sessionStorage.getItem('mapLoaded');
    return wasLoaded !== 'true';
  });
  const [mapReady, setMapReady] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapViewType, setMapViewType] = useState(getInitialMapViewType);
  const MAX_VISIBLE_POINTS = 450;

  const handleMapViewChange = useCallback((viewType) => {
    setMapViewType(viewType);
    try {
      sessionStorage.setItem("mapViewType", viewType);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (mapRef.current && mapReady) {
      applyMapViewType(mapRef.current, mapViewType);
    }
  }, [mapViewType, mapReady]);

  const updateBoundsState = (mapInstance) => {
    if (!mapInstance) return;
    const bounds = mapInstance.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    setMapBounds({
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng()
    });
  };
  const fitBoundsToMarkers = () => {
    if (mapRef.current && points.length > 0) {
      // Primeiro, faz uma animação suave para uma visão mais ampla
      // para depois aproximar - cria um efeito visual de "zoom para dentro"
      const initialZoomLevel = 4;

      // Se a visualização atual estiver muito aproximada, primeiro afasta gentilmente
      if (mapRef.current.getZoom() > initialZoomLevel) {
        mapRef.current.setOptions({ animation: window.google.maps.Animation.BOUNCE });
      }

      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((point) => {
        const [lng, lat] = point.geometry.coordinates;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          bounds.extend(new window.google.maps.LatLng(lat, lng));
        }
      });

      // Se houver apenas um ponto, faça uma animação de zoom suave
      if (points.length === 1) {
        const [lng, lat] = points[0].geometry.coordinates;
        const targetLatLng = new window.google.maps.LatLng(lat, lng);

        // Primeiro centraliza com uma transição suave
        mapRef.current.panTo(targetLatLng);

        // Depois aplica o zoom com delay para criar efeito de abertura
        setTimeout(() => {
          mapRef.current.setZoom(15);
        }, 300);
        return;
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const latDiff = ne.lat() - sw.lat();
      const lngDiff = ne.lng() - sw.lng();

      // Para pontos muito próximos, faça uma animação suave
      if (latDiff < 0.05 && lngDiff < 0.05) {
        const centerLat = (ne.lat() + sw.lat()) / 2;
        const centerLng = (ne.lng() + sw.lng()) / 2;
        const targetLatLng = new window.google.maps.LatLng(centerLat, centerLng);

        // Transição suave para o centro
        mapRef.current.panTo(targetLatLng);

        // Anima o zoom após a centralização
        setTimeout(() => {
          mapRef.current.setZoom(14);
        }, 300);
        return;
      }

      // Para áreas pequenas, anim suave
      else if (latDiff < 0.5 && lngDiff < 0.5) {
        const centerLat = (ne.lat() + sw.lat()) / 2;
        const centerLng = (ne.lng() + sw.lng()) / 2;
        const targetLatLng = new window.google.maps.LatLng(centerLat, centerLng);

        mapRef.current.panTo(targetLatLng);

        setTimeout(() => {
          mapRef.current.setZoom(12);
        }, 300);
        return;
      }

      // Para áreas mais amplas, use fitBounds com animação
      // Define opções de limites para uma animação mais suave
      const fitBoundsOptions = {
        padding: { top: 30, right: 30, bottom: 30, left: 30 },
        duration: 800 // duração da animação em milissegundos
      };

      // Aplica fitBounds com animação (quando disponível)
      try {
        mapRef.current.fitBounds(bounds, fitBoundsOptions);
      } catch (e) {
        // Fallback para versões que não suportam as opções de animação
        mapRef.current.fitBounds(bounds);
      }

    }
  };

  useEffect(() => {
    if (points.length > 0 && mapRef.current && mapReady) {
      const timer = setTimeout(() => {
        fitBoundsToMarkers();
        updateBoundsState(mapRef.current);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [points, mapReady]);

  const visiblePoints = useMemo(() => {
    if (!points.length) {
      return [];
    }

    if (!mapReady || !mapBounds) {
      return points.slice(0, MAX_VISIBLE_POINTS);
    }

    const latMargin = (mapBounds.north - mapBounds.south) * 0.25;
    const lngMargin = (mapBounds.east - mapBounds.west) * 0.25;

    const filtered = points.filter((point) => {
      const [lng, lat] = point.geometry.coordinates;
      if (!lng || !lat) {
        return false;
      }

      return (
        lat >= mapBounds.south - latMargin &&
        lat <= mapBounds.north + latMargin &&
        lng >= mapBounds.west - lngMargin &&
        lng <= mapBounds.east + lngMargin
      );
    });

    if (filtered.length === 0) {
      return points.slice(0, MAX_VISIBLE_POINTS);
    }

    return filtered.slice(0, MAX_VISIBLE_POINTS);
  }, [points, mapBounds, mapReady]);

  const onlineSerials = useMemo(() => {
    const set = new Set();
    (equipmentsStatus || []).forEach((item) => {
      if (item?.status === "online" && item?.serialnumber) {
        set.add(item.serialnumber);
      }
    });
    return set;
  }, [equipmentsStatus]);

  // Efeito para mostrar o loader quando os points mudam drasticamente
  useEffect(() => {
    if (points.length === 0) {
      setMapLoading(false); // Não mostrar loader se não há pontos
    }
  }, [points]);

  // Efeito para esconder o loader rapidamente se o mapa já foi carregado antes
  useEffect(() => {
    const wasLoaded = sessionStorage.getItem('mapLoaded') === 'true';
    if (wasLoaded && mapRef.current) {
      // Se o mapa já foi carregado antes, remove o loader quase instantaneamente
      const timer = setTimeout(() => {
        setMapLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Limpa o cache do mapa ao desmontar o componente (quando sair da página)
  useEffect(() => {
    return () => {
      // Mantém o cache apenas durante a sessão
      // Se quiser limpar ao sair da página, descomente a linha abaixo:
      // sessionStorage.removeItem('mapLoaded');
    };
  }, []);

  // Efeito para desenhar a linha quando isHistoricMode for true
  useEffect(() => {
    let timerDrawLine;
    let icons = [];

    if (mapRef.current && points.length > 0 && isHistoricMode) {
      // Remove a polyline anterior se existir
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }

      // Aguarda um pouco para garantir que os points estejam atualizados
      timerDrawLine = setTimeout(() => {
        // Ajusta o zoom automaticamente para o caminho completo
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach(point => {
          const [lng, lat] = point.geometry.coordinates;
          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        });

        // Adiciona margem ao bounds para melhor visualização
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latMargin = (ne.lat() - sw.lat()) * 0.2;
        const lngMargin = (ne.lng() - sw.lng()) * 0.2;
        bounds.extend(new window.google.maps.LatLng(ne.lat() + latMargin, ne.lng() + lngMargin));
        bounds.extend(new window.google.maps.LatLng(sw.lat() - latMargin, sw.lng() - lngMargin));

        // Aplica o ajuste de zoom
        mapRef.current.fitBounds(bounds);

        // Cria o caminho com as coordenadas dos pontos
        const path = points.map(point => {
          const [lng, lat] = point.geometry.coordinates;
          return new window.google.maps.LatLng(lat, lng);
        });

        const arrowSymbol = {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          fillOpacity: 1,
          scale: 5, // Aumento do tamanho da seta
          fillColor: '#1c4220',
          strokeColor: '#8fad15',
          strokeWeight: 2
        };

        // Adiciona marcadores de início, meio e fim se tivermos pelo menos 3 pontos
        if (path.length >= 3) {
          // Marcador de início (círculo)
          const startMarker = new window.google.maps.Marker({
            position: path[0],
            map: mapRef.current,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: '#1c4220',
              fillOpacity: 1,
              strokeColor: '#58595b',
              strokeWeight: 2,
              scale: 8 // Aumentado para melhor visibilidade
            },
            title: 'Início',
            zIndex: 1000 // Para garantir que fique acima de outros marcadores
          });

          icons.push(startMarker);

          // Marcador do meio (apenas se tivermos mais de 3 pontos)
          if (path.length > 3) {
            const midIndex = Math.floor(path.length / 2);
            const midMarker = new window.google.maps.Marker({
              position: path[midIndex],
              map: mapRef.current,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: '#8fad15',
                fillOpacity: 1,
                strokeColor: '#58595b',
                strokeWeight: 2,
                scale: 7 // Aumentado para melhor visibilidade
              },
              title: 'Meio',
              zIndex: 1000
            });

            icons.push(midMarker);
          }

          // Marcador de fim (quadrado)
          const endMarker = new window.google.maps.Marker({
            position: path[path.length - 1],
            map: mapRef.current,
            icon: {
              path: 'M -6,-6 6,-6 6,6 -6,6 Z', // Aumentado o tamanho
              fillColor: '#1c4220',
              fillOpacity: 1,
              strokeColor: '#58595b',
              strokeWeight: 2,
              scale: 1.5 // Aumentado para melhor visibilidade
            },
            title: 'Fim',
            zIndex: 1000
          });

          icons.push(endMarker);
        }

        // Adiciona uma linha secundária mais grossa como "efeito de brilho"
        const glowLine = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: '#58595b',
          strokeOpacity: 0.6,
          strokeWeight: 7, // Mais grossa que a linha principal
          map: mapRef.current,
          zIndex: 1
        });

        icons.push(glowLine); // Adiciona à lista para limpeza posterior

        // Cria uma linha sólida estilizada para substituir o tracejado que não funcionou
        const solidLine = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: '#1c4220',
          strokeOpacity: 0.9,
          strokeWeight: 4,
          map: mapRef.current,
          zIndex: 2
        });

        icons.push(solidLine);

        // Adiciona o efeito de tracejado através de símbolos repetidos
        const dashedLine = new window.google.maps.Polyline({
          path: path,
          strokeOpacity: 0,
          icons: [{
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeWeight: 3,
              scale: 3,
              strokeColor: '#FFFFFF' // Branco para criar o efeito tracejado
            },
            offset: '0',
            repeat: '10px'
          }],
          map: mapRef.current,
          zIndex: 3
        });

        icons.push(dashedLine);

        // Adiciona setas na linha
        const arrowLine = new window.google.maps.Polyline({
          path: path,
          strokeOpacity: 0,
          icons: [
            {
              icon: arrowSymbol,
              offset: '30%'
            },
            {
              icon: arrowSymbol,
              offset: '70%'
            },
            {
              icon: arrowSymbol,
              offset: '100%'
            }
          ],
          map: mapRef.current,
          zIndex: 4
        });

        icons.push(arrowLine);

        // Referência principal para limpeza posterior
        polylineRef.current = solidLine;

        // Adiciona pequenos círculos em cada ponto do caminho para destacar os pontos
        path.forEach((point, index) => {
          if (index > 0 && index < path.length - 1) { // Exclui o primeiro e último (já têm marcadores)
            const pointMarker = new window.google.maps.Marker({
              position: point,
              map: mapRef.current,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: '#8fad15',
                fillOpacity: 0.7,
                strokeColor: '#1c4220',
                strokeWeight: 1,
                scale: 4
              },
              title: `Ponto ${index + 1}`,
              zIndex: 999
            });

            icons.push(pointMarker);
          }
        });

      }, 1000); // Aguarda 1 segundo antes de desenhar a linha

    } else if (polylineRef.current && !isHistoricMode) {
      // Remove a polyline se isHistoricMode for false
      polylineRef.current.setMap(null);
      polylineRef.current = null;

      // Remove todos os ícones do mapa
      if (icons.length > 0) {
        icons.forEach(icon => icon.setMap(null));
        icons = [];
      }
    }

    // Limpa o timer ao desmontar o componente ou quando as dependências mudarem
    return () => {
      if (timerDrawLine) {
        clearTimeout(timerDrawLine);
      }

      // Remove todos os ícones do mapa ao desmontar
      if (icons.length > 0) {
        icons.forEach(icon => icon.setMap(null));
      }
    };
  }, [points, isHistoricMode, mapRef.current]);

  const findCoordinatesInPoints = (serial) => {
    const foundPoint = points.find((point) => point.properties.data.serial === serial);
    return foundPoint ? foundPoint.geometry.coordinates : null;
  };

  const medidaCoordinates = medida ? findCoordinatesInPoints(medida.serialnumber) : null;

  const getMarkerImageSrc = (serial) => {
    if (!serial) return "/images/greenMarker.png";
    if (serial.includes('SEMT')) return "/images/999dry.png";
    if (serial.includes('SIMT')) return "/images/esi.png";
    if (serial.includes('EEMT')) return "/images/ee.png";
    if (serial.includes('BIMT')) return "/images/fbi.png";
    if (serial.includes('CNMT')) return "/images/cn.png";
    return "/images/greenMarker.png";
  };

  useEffect(() => {
    if (medidaCoordinates) {

      setMedidaQueue([]);

      const newMedida = {
        serial: medida.serialnumber,
        umidade: medida.humidity,
        grain: medida.grain,
        ph: medida.ph,
        temperature: medida.temperature,
        coordinates: medidaCoordinates
      };

      setMedidaQueue([newMedida]);

      const timer = setTimeout(() => {
        setMedidaQueue((prevQueue) =>
          prevQueue.filter((m) => m.serial !== newMedida.serial)
        );
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [medida]);

  // Dentro do componente Map, modifico a função para lidar com a mudança de período
  const handlePeriodChange = (period) => {
    // Se o period for uma string vazia, desativa o modo histórico
    if (period === "") {
      setHistoricEquipment(null);
      return;
    }

    if (isHistoricMode) {
      const selectedSerial = points[0]?.properties?.data?.serial;
      if (selectedSerial) {
        setHistoricEquipment(selectedSerial, period);
      }
    }
  };

  const wasMapLoaded = sessionStorage.getItem('mapLoaded') === 'true';

  return (
    <div className="map-container">

      <MapLoader isLoading={mapLoading} />

      <div className={`map-content ${!mapLoading ? 'loaded' : ''} ${wasMapLoaded ? 'fast-load' : ''}`}>
        <Filter
          user={user}
          filter={filter}
          cooperative={cooperative}
          companyNames={companyNames}
          stateNames={stateNames}
          typeEquipaments={typeEquipaments}
          grains={grains}
          activeFilters={activeFilters}
          handleExportAllEquipments={handleExportAllEquipments}
        />

        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
          defaultCenter={center}
          defaultZoom={4.5}
          options={MAP_OPTIONS}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map }) => {
            mapRef.current = map;
            applyMapViewType(map, mapViewType);
            setMapReady(true);
            updateBoundsState(map);

            // Ajusta o mapa aos pontos assim que for carregado
            if (points.length > 0) {
              fitBoundsToMarkers();
            }

            // Adiciona listener para detectar interação do usuário com o mapa
            map.addListener('drag', () => {
              userInteractedRef.current = true;
            });

            map.addListener('zoom_changed', () => {
              userInteractedRef.current = true;
            });

            map.addListener('idle', () => {
              updateBoundsState(map);
            });

            // Remove o loader após o mapa estar totalmente carregado
            window.google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
              // Se o mapa já foi carregado antes, remove o loader imediatamente
              const wasLoaded = sessionStorage.getItem('mapLoaded') === 'true';
              const delay = wasLoaded ? 0 : 400;

              // Aguarda um pouco mais para garantir que tudo está renderizado
              setTimeout(() => {
                setMapLoading(false);
                // Marca que o mapa foi carregado
                sessionStorage.setItem('mapLoaded', 'true');
                mapLoadedRef.current = true;
              }, delay);
            });
          }}
        >
          {visiblePoints.map((point, index) => {
            const [longitude, latitude] = point.geometry.coordinates;
            const { serial } = point.properties.data;
            const daysToActivate = point?.properties?.data?.daystoactivate;
            if (!latitude || !longitude || !serial) return null; // Evita erros com dados inválidos
            const markerSrc = getMarkerImageSrc(serial);
            const isHighlighted = highlightedSerials.includes(serial);
            const isAlert = (daysToActivate === 0 || daysToActivate === "0") && !isHistoricMode;
            const isOnline = onlineSerials.has(serial);

            const markerClassNames = [
              "marker-icon",
              isHighlighted ? "marker-icon--highlighted" : "",
              isAlert ? "marker-icon--alert" : ""
            ].join(" ");

            const handleMarkerClick = () => {
              if (isHistoricMode) {
                console.log("Click não funciona no modo histórico");
                return;
              }
              handleSearchData(serial);
            };

            return (
              <Marker
                key={`equip-${serial}-${index}`}
                lat={latitude}
                lng={longitude}
                onMouseEnter={() => setHoveredMarker(serial)}
                onMouseLeave={() => setHoveredMarker(null)}
              >
                <div className="equip-marker-wrap">
                  <div className={markerClassNames}>
                    {isOnline && (
                      <span
                        className="equip-marker-online-dot"
                        title="Equipamento online"
                        aria-hidden
                      />
                    )}
                    <button
                      type="button"
                      className="equip-marker-btn"
                      onClick={handleMarkerClick}
                      aria-label={`Equipamento ${serial}${isOnline ? " (online)" : ""}`}
                    >
                      <img
                        alt={`Equipamento ${serial}`}
                        src={markerSrc}
                        className="equip-marker-img"
                      />
                    </button>
                  </div>
                </div>
                {hoveredMarker === serial && !isHistoricMode && (
                  <MarkerLabel
                    medida={equipmentsStatus.filter(item => item.serialnumber === serial)[0]?.measurement}
                  />
                )}
              </Marker>
            );
          })}

          {medidaQueue.map((m, index) => {
            const markerSrc = getMarkerImageSrc(m.serial);
            return (
              <Marker key={`medida-${m.serial}-${index}`} lat={m.coordinates[1]} lng={m.coordinates[0]}>
                <div className="marker-icon marker-icon--pulse">
                  <button
                    type="button"
                    className="equip-marker-btn"
                    onClick={() => setSelectedEquipment(m)}
                    aria-label={`Medida ${m.serial}`}
                  >
                    <img
                      alt={`Medida ${m.serial}`}
                      src={markerSrc}
                      className="equip-marker-img"
                    />
                  </button>
                </div>
                <MarkerLabel medida={m} />
              </Marker>
            );
          })}

        </GoogleMapReact>

        <div
          className="map-view-switcher"
          role="group"
          aria-label="Tipo de visualização do mapa"
        >
          <button
            type="button"
            className={`map-view-switcher__btn ${mapViewType === MAP_VIEW_ROADMAP ? "map-view-switcher__btn--active" : ""}`}
            onClick={() => handleMapViewChange(MAP_VIEW_ROADMAP)}
            aria-pressed={mapViewType === MAP_VIEW_ROADMAP}
            title="Visualização em mapa"
          >
            <MdMap size={18} aria-hidden />
            <span>Mapa</span>
          </button>
          <button
            type="button"
            className={`map-view-switcher__btn ${mapViewType === MAP_VIEW_SATELLITE ? "map-view-switcher__btn--active" : ""}`}
            onClick={() => handleMapViewChange(MAP_VIEW_SATELLITE)}
            aria-pressed={mapViewType === MAP_VIEW_SATELLITE}
            title="Visualização por satélite"
          >
            <MdSatelliteAlt size={18} aria-hidden />
            <span>Satélite</span>
          </button>
        </div>

        {isHistoricMode && (
          <Timeline
            points={points}
            medida={medida}
            currentPeriod={currentPeriod}
            equipmentsStatus={equipmentsStatus}
            onPointSelect={(point, index) => {
              // Centraliza o mapa no ponto selecionado e aplica um zoom mais próximo
              if (mapRef.current && point) {
                const [lng, lat] = point.geometry.coordinates;

                // Sempre centraliza no ponto selecionado
                mapRef.current.setCenter({ lat: lat, lng: lng });

                // Aplica zoom apenas se o usuário ainda não interagiu com o mapa
                // ou se o zoom atual for muito distante
                const currentZoom = mapRef.current.getZoom();
                if (!userInteractedRef.current || currentZoom < 10) {

                  // Adiciona um listener de zoom que é acionado apenas uma vez
                  // para marcar que o usuário interagiu com o mapa
                  if (!mapRef.current.zoomListener) {
                    mapRef.current.zoomListener = true;
                    window.google.maps.event.addListenerOnce(mapRef.current, 'zoom_changed', () => {
                      // Este evento será acionado na próxima mudança de zoom após esta configuração
                      // feita pelo usuário (não pela timeline)
                      userInteractedRef.current = true;
                    });
                  }
                }
              }
            }}
            onPeriodChange={handlePeriodChange}
          />
        )}
      </div>

    </div>
  );
};

export default Map;
